<template>
  <div class="cart-popup row c-mx-1 c-p-0">
    <div
      v-if="isFreeTransportNotice"
      class="progress-bar"
    >
      <div
        class="progress-percent"
        :style="{ width: `${percent}%` }"
      />
      <div
        v-if="hasFreeTransport || (isSignedIn && getCurrentCustomer.inPlusClub)"
        class="progress-text absolute left-0 top-0"
      >
        {{ t('you_have_free_shipping') }}
      </div>
      <div
        v-else
        class="progress-text absolute left-0 top-0"
      >
        {{ t('missing_free_shipping') }} {{ numberToCurrency(amount, t('currency')) }}
      </div>
    </div>
    <div class="row c-p-0 c-m-0 c-w-100 c-pt-3 c-pb-3">
      <div
        v-for="cartMix in getCartMixes"
        :key="cartMix.id"
        class="col-6 flex c-mb-3"
      >
        <div class="col-4 c-pl-0 relative">
          <div class="embed-responsive embed-responsive-4by3 c-mb-0">
            <RemoteImage
              :src="cartMix.tube"
              class="embed-responsive-item image-contain"
              bird-class="embed-responsive-item image-contain"
              :alt="cartMix.name"
            />
          </div>
          <span class="inline-block c-p-2 absolute top-0 right-0 mr-n1 bg-danger c-rounded-circle">
            <span class="absolute centered text-sm-c text-white c-leading-1">
              {{ cartMix.quantity }}
            </span>
          </span>
        </div>
        <div class="col-8 c-pr-0">
          <h2 class="c-mb-4 c-lg:c-mb-0 text-sm-c font-family-base c-leading-base font-medium text-primary">
            {{ cartMix.name }}
          </h2>
        </div>
      </div>
      <div
        v-for="product in getCartProducts"
        :key="product.id"
        class="col-6 flex c-mb-3"
      >
        <div class="col-4 c-pl-0 relative">
          <div class="embed-responsive c-mb-0">
            <RemoteResponsiveImage
              v-if="product.images.length > 0"
              :product-image-data="product.images[0]?.data"
              img-class="embed-responsive-item relative image-contain"
              :alt="product.images[0]?.alt || product.name"
              sizes="148px"
            />
          </div>
          <span class="inline-block c-p-2 absolute top-0 right-0 mr-n1 bg-danger c-rounded-circle">
            <span class="absolute centered text-sm-c text-white c-leading-1">
              {{ product.quantity }}
            </span>
          </span>
        </div>
        <div class="col-8 c-pr-0">
          <h2 class="c-mb-4 c-lg:c-mb-0 text-sm-c font-family-base c-leading-base font-medium">
            <router-link
              :to="{ name: 'productPath', params: { permalink: product.permalink } }"
              class="text-dration-none"
            >
              {{ product.name }}
            </router-link>
          </h2>
        </div>
      </div>
    </div>
    <div
      v-if="getTotalItemsInCart === 0"
      class="c-w-100 c-pb-3 text-center c-pt-3"
    >
      <p class="text-primary">
        {{ t('must_first_add_items') }}
      </p>
    </div>
    <div class="block text-center c-pb-3 c-w-100">
      <router-link
        :to="{ name: 'cartsPath' }"
        class="btn  btn-success c-px-5 c-xl:c-px-7 c-py-2 font-medium c-rounded-pill"
      >
        {{ t('to_cart') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'

const appStore = useAppStore()
const { isFreeTransportNotice, isSignedIn, getCurrentCustomer } = storeToRefs(appStore)

const { t } = useI18n()

defineProps({
  amount: {
    type: Number,
    default: 0,
  },
  percent: {
    type: Number,
    default: 0,
  },
})

const cartStore = useCartStore()
const { hasFreeTransport, getCartProducts, getCartMixes, getTotalItemsInCart } = storeToRefs(cartStore)
</script>

<i18n>
  {
    "cs": {
    "currency": "Kč",
    "free_shipping_from": "Doprava zdarma od 1499 Kč",
    "missing_free_shipping": "Do dopravy zdarma chybí",
    "must_first_add_items": "Nejprve musíte do košíku vložit zboží.",
    "to_cart": "Do košíku",
    "you_have_free_shipping": "Dopravu máte zdarma! 🎉"
},
    "sk": {
    "currency": "€",
    "free_shipping_from": "Doprava zdarma od 59 €",
    "missing_free_shipping": "Do dopravy zadarmo chýba",
    "must_first_add_items": "Najprv musíte do košíka vložiť tovar.",
    "to_cart": "Do košíka",
    "you_have_free_shipping": "Dopravu máte zdarma! 🎉"
},
    "pl": {
    "currency": "zł",
    "free_shipping_from": "Darmowa dostawa od 199 zł",
    "missing_free_shipping": "Do darmowej dostawy brakuje",
    "must_first_add_items": "Najpierw włóż produkty do koszyka.",
    "to_cart": "Do koszyka",
    "you_have_free_shipping": "Masz darmową dostawę! 🎉"
},
    "hu": {
    "currency": "Ft",
    "free_shipping_from": "Ingyenes szállítás 18990 Ft felett",
    "missing_free_shipping": "Az ingyenes kiszállításhoz hiányzik",
    "must_first_add_items": "Előbb tegyél valamit a kosárba.",
    "to_cart": "Kosárba",
    "you_have_free_shipping": "A kiszállítás számodra ingyenes! 🎉"
},
    "de": {
    "currency": "EUR",
    "free_shipping_from": "Kostenloser Versand ab 49 EUR",
    "missing_free_shipping": "Bis zum kostenlosen Versand fehlen",
    "must_first_add_items": "Zuerst muss ein Produkt in den Warenkorb gelegt werden.",
    "to_cart": "Hinzufügen",
    "you_have_free_shipping": "Kostenloser Versand! 🎉"
},
    "at": {
    "currency": "EUR",
    "free_shipping_from": "Kostenloser Versand ab 49 EUR",
    "missing_free_shipping": "Bis zum kostenlosen Versand fehlen",
    "must_first_add_items": "Zuerst muss ein Produkt in den Warenkorb gelegt werden.",
    "to_cart": "Hinzufügen",
    "you_have_free_shipping": "Kostenloser Versand! 🎉"
},
    "ro": {
    "currency": "Lei",
    "free_shipping_from": "Transport gratuit de la 199 Lei",
    "missing_free_shipping": "Până la transport gratuit lipsesc",
    "must_first_add_items": "Mai întâi adaugă produse în coș.",
    "to_cart": "În coș",
    "you_have_free_shipping": "Aveți transport gratuit! 🎉"
},
    "fr": {
    "currency": "€",
    "free_shipping_from": "Livraison gratuite à partir de 49 €",
    "missing_free_shipping": "Pour bénéficier de la livraison gratuite, il vous manque",
    "must_first_add_items": "Vous devez d’abord ajouter des articles au panier.",
    "to_cart": "Au panier",
    "you_have_free_shipping": "Vous bénéficiez de la livraison gratuite ! 🎉"
},
    "it": {
    "currency": "€",
    "free_shipping_from": "Spedizione gratuita a partire da 49 €",
    "missing_free_shipping": "Alla spedizione gratuita manca",
    "must_first_add_items": "Prima devi aggiungere dei prodotti al carrello.",
    "to_cart": "Al carrello",
    "you_have_free_shipping": "Hai la spedizione gratuita! 🎉"
}
  }
</i18n>

<style lang="scss" scoped>
.cart-popup {
  margin: 0px !important;

  .progress-bar {
    width: 100%;
    background-color: #4696ef;
    height: 25px;

    .progress-percent {
      background-color: #22548c;
      max-width: 100%;
      height: 100%;
      transition: 0.5s width;
    }
    .progress-text {
      text-align: center;
      width: 100%;
      z-index: 2;
      color: $white;
      font-weight: 700;
      font-size: 14px;
      padding: 3px 20px;
    }
  }
}
</style>
